import { SystemStyleObject } from '@chakra-ui/react'
import { keyframes } from '@emotion/react'

import { tokenVar } from '../../theme/tokenVar'
import type { ColorScheme } from '../../types'
import type { TableDensity, TableVariant } from './TableContext'

const skeletonPulse = keyframes`
  0%, 100% { opacity: 0.35; }
  50%      { opacity: 0.7; }
`

// ─── Token map ────────────────────────────────────────────────────────────────

const tokens = {
  dark: {
    containerBg: 'bg.card',
    embeddedHeaderBg: 'bg.card-elevated',
    borderColor: 'border.subtle',
    rowBg: 'bg.table-row',
    rowBgHover: 'bg.table-row-hover',
    rowBgAlt: 'bg.table-row-alt',
    selectedRowBg: 'bg.table-row-selected',
    headerText: 'text.secondary',
    cellText: 'text.primary',
    tabActiveBg: 'bg.tab-active',
    tabActiveText: 'text.primary',
    tabInactiveText: 'text.secondary',
    inputBg: 'bg.input',
    inputText: 'text.secondary',
    iconSecondary: 'icon.secondary',
    focusRing: 'interactive.focus-ring',
    checkboxBorder: 'interactive.tertiary-border',
    // Same brand accent as every other checked selection control
    // (interactive/radio-on).
    checkboxBg: 'interactive.radio-on',
    checkboxIcon: tokenVar('colors.icon.on-accent'),
    perPageSelectBorder: 'interactive.tertiary-border',
  },
  light: {
    containerBg: 'light.bg.card',
    embeddedHeaderBg: 'light.bg.card-elevated',
    borderColor: 'light.border.subtle',
    rowBg: 'light.bg.table-row',
    rowBgHover: 'light.bg.table-row-hover',
    rowBgAlt: 'light.bg.table-row-alt',
    selectedRowBg: 'light.bg.table-row-selected',
    headerText: 'light.text.secondary',
    cellText: 'light.text.primary',
    tabActiveBg: 'light.bg.tab-active',
    tabActiveText: 'light.text.on-accent',
    tabInactiveText: 'light.text.secondary',
    inputBg: 'light.bg.input',
    inputText: 'light.text.secondary',
    iconSecondary: 'light.icon.secondary',
    focusRing: 'light.interactive.focus-ring',
    checkboxBorder: 'light.interactive.tertiary-border',
    checkboxBg: 'light.interactive.radio-on',
    checkboxIcon: '#FFFFFF',
    perPageSelectBorder: 'light.interactive.tertiary-border',
  },
} as const

// ─── Table container ──────────────────────────────────────────────────────────

export const tableContainerStyles = (
  _colorScheme: ColorScheme,
  height?: string,
  maxWidth?: string
): SystemStyleObject => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  ...(height !== undefined && { height }),
  ...(maxWidth !== undefined && { maxWidth, margin: '0 auto' }),
})

/** Inner scroll wrapper — wraps header + body, excludes pagination. */
export const tableScrollAreaStyles = (
  colorScheme: ColorScheme
): SystemStyleObject => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  minWidth: 0,
  overflowX: 'auto',
  flex: 1,
  minHeight: 0,
  bg: tokens[colorScheme].containerBg,
})

// ─── Header ───────────────────────────────────────────────────────────────────

export const tableHeaderStyles = (
  colorScheme: ColorScheme,
  density: TableDensity = 'default',
  variant: TableVariant = 'default'
): SystemStyleObject => ({
  display: 'flex',
  width: '100%',
  height: density === 'compact' ? '32px' : '40px',
  borderColor: tokens[colorScheme].borderColor,
  borderStyle: 'solid',
  ...(variant === 'embedded'
    ? {
        borderWidth: 0,
        borderBottomWidth: '1px',
        bg: tokens[colorScheme].embeddedHeaderBg,
      }
    : { borderWidth: '1px' }),
})

export const tableHeaderCellStyles = (
  sortable: boolean,
  density: TableDensity = 'default'
): SystemStyleObject => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  height: density === 'compact' ? '32px' : '40px',
  px: density === 'compact' ? '10px' : '16px',
  gap: '8px',
  overflow: 'hidden',
  ...(sortable && { cursor: 'pointer', userSelect: 'none' }),
})

export const tableHeaderCellLabelStyles = (
  colorScheme: ColorScheme
): SystemStyleObject => ({
  textStyle: 'caption-medium',
  textTransform: 'uppercase',
  color: tokens[colorScheme].headerText,
  whiteSpace: 'nowrap',
  flexShrink: 0,
})

// ─── Body ─────────────────────────────────────────────────────────────────────

export const tableBodyStyles: SystemStyleObject = {
  flex: 1,
  overflowY: 'auto',
  minHeight: 0,
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

export const tableSkeletonBarStyles = (
  colorScheme: ColorScheme,
  density: TableDensity = 'default'
): SystemStyleObject => ({
  height: density === 'compact' ? '12px' : '16px',
  width: '95%',
  borderRadius: '4px',
  bg: tokens[colorScheme].borderColor,
  animation: `${skeletonPulse} 1.4s ease-in-out infinite`,
})

// ─── Row ──────────────────────────────────────────────────────────────────────

export const tableRowStyles = (
  colorScheme: ColorScheme,
  isClickable: boolean,
  isSelected: boolean,
  isAlternate = false,
  density: TableDensity = 'default',
  variant: TableVariant = 'default'
): SystemStyleObject => ({
  display: 'flex',
  width: '100%',
  height:
    density === 'compact'
      ? '36px'
      : density === 'comfortable'
      ? '64px'
      : '56px',
  flexShrink: 0,
  bg: isSelected
    ? tokens[colorScheme].selectedRowBg
    : isAlternate
    ? tokens[colorScheme].rowBgAlt
    : tokens[colorScheme].rowBg,
  borderBottomWidth: '1px',
  borderBottomColor: tokens[colorScheme].borderColor,
  borderBottomStyle: 'solid',
  // An embedded table's container draws its sides.
  ...(variant === 'default' && {
    borderLeftWidth: '1px',
    borderLeftColor: tokens[colorScheme].borderColor,
    borderLeftStyle: 'solid',
    borderRightWidth: '1px',
    borderRightColor: tokens[colorScheme].borderColor,
    borderRightStyle: 'solid',
  }),
  ...(isClickable && {
    cursor: 'pointer',
    _hover: {
      bg: isSelected
        ? tokens[colorScheme].selectedRowBg
        : tokens[colorScheme].rowBgHover,
    },
  }),
})

// ─── Cell ─────────────────────────────────────────────────────────────────────

export const tableCellStyles = (
  colorScheme: ColorScheme,
  align: 'left' | 'center' | 'right',
  density: TableDensity = 'default'
): SystemStyleObject => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent:
    align === 'center'
      ? 'center'
      : align === 'right'
      ? 'flex-end'
      : 'flex-start',
  height:
    density === 'compact'
      ? '36px'
      : density === 'comfortable'
      ? '64px'
      : '56px',
  px: density === 'compact' ? '10px' : '16px',
  overflow: 'hidden',
  color: tokens[colorScheme].cellText,
  fontFamily: "'Soehne', sans-serif",
  fontSize: density === 'compact' ? '12px' : '16px',
  fontStyle: 'normal',
  fontWeight: '500',
  lineHeight: density === 'compact' ? '16px' : '24px',
})

// ─── Sort indicator ───────────────────────────────────────────────────────────

export const sortIndicatorContainerStyles: SystemStyleObject = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '2px',
  width: '12px',
  height: '18px',
  flexShrink: 0,
}

export const sortIndicatorActiveContainerStyles: SystemStyleObject = {
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  flexShrink: 0,
}

export const sortBadgeTextStyles: SystemStyleObject = {
  fontSize: '10px',
  fontWeight: '600',
}

/** Resolved CSS-variable strings for sort arrow fill colors. */
export const sortArrowColors = (colorScheme: ColorScheme) => ({
  active:
    colorScheme === 'light'
      ? tokenVar('colors.light.text.primary')
      : tokenVar('colors.text.primary'),
  inactive:
    colorScheme === 'light'
      ? tokenVar('colors.light.icon.secondary')
      : tokenVar('colors.icon.secondary'),
})

// ─── Pagination ───────────────────────────────────────────────────────────────

export const paginationStyles = (
  colorScheme: ColorScheme,
  density: TableDensity = 'default'
): SystemStyleObject => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  height: density === 'compact' ? '40px' : '56px',
  px: density === 'compact' ? '10px' : '16px',
  py: density === 'compact' ? '8px' : '12px',
  bg: 'transparent',
  borderTopWidth: '1px',
  borderTopColor: tokens[colorScheme].borderColor,
  borderTopStyle: 'solid',
  flexShrink: 0,
})

export const paginationRangeStyles = (
  colorScheme: ColorScheme,
  density: TableDensity = 'default'
): SystemStyleObject => ({
  fontFamily: "'Soehne', sans-serif",
  fontWeight: '400',
  fontSize: density === 'compact' ? '12px' : '16px',
  lineHeight: density === 'compact' ? '16px' : '24px',
  color: tokens[colorScheme].tabInactiveText,
  whiteSpace: 'nowrap',
  flexShrink: 0,
})

export const paginationControlsStyles = (
  density: TableDensity = 'default'
): SystemStyleObject => ({
  display: 'flex',
  alignItems: 'center',
  gap: density === 'compact' ? '4px' : '8px',
  flexShrink: 0,
})

export const paginationPageButtonStyles = (
  colorScheme: ColorScheme,
  isActive: boolean,
  density: TableDensity = 'default'
): SystemStyleObject => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: density === 'compact' ? '24px' : '32px',
  height: density === 'compact' ? '24px' : '32px',
  borderRadius: density === 'compact' ? '12px' : '16px',
  cursor: 'pointer',
  border: 'none',
  bg: isActive ? tokens[colorScheme].tabActiveBg : 'transparent',
  color: isActive
    ? tokens[colorScheme].tabActiveText
    : tokens[colorScheme].tabInactiveText,
  fontSize: density === 'compact' ? '12px' : '16px',
  lineHeight: density === 'compact' ? '16px' : '24px',
  fontWeight: isActive ? '500' : '400',
  fontFamily: "'Soehne', 'Söhne', sans-serif",
  flexShrink: 0,
  _hover: {
    bg: isActive
      ? tokens[colorScheme].tabActiveBg
      : tokens[colorScheme].rowBgAlt,
  },
  _focusVisible: {
    outline: '2px solid',
    outlineColor: tokens[colorScheme].focusRing,
    outlineOffset: '2px',
  },
})

export const paginationNavButtonStyles = (
  colorScheme: ColorScheme,
  disabled: boolean,
  density: TableDensity = 'default'
): SystemStyleObject => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: density === 'compact' ? '24px' : '32px',
  height: density === 'compact' ? '24px' : '32px',
  borderRadius: density === 'compact' ? '12px' : '16px',
  cursor: disabled ? 'not-allowed' : 'pointer',
  border: 'none',
  bg: 'transparent',
  color: tokens[colorScheme].tabInactiveText,
  opacity: disabled ? 0.4 : 1,
  flexShrink: 0,
  ...(!disabled && { _hover: { bg: tokens[colorScheme].rowBgAlt } }),
  _focusVisible: {
    outline: '2px solid',
    outlineColor: tokens[colorScheme].focusRing,
    outlineOffset: '2px',
  },
})

export const paginationPerPageStyles = (
  density: TableDensity = 'default'
): SystemStyleObject => ({
  display: 'flex',
  alignItems: 'center',
  gap: density === 'compact' ? '8px' : '12px',
  flexShrink: 0,
})

export const paginationPerPageLabelStyles = (
  colorScheme: ColorScheme,
  density: TableDensity = 'default'
): SystemStyleObject => ({
  fontFamily: "'Soehne', 'Söhne', sans-serif",
  fontWeight: '400',
  fontSize: density === 'compact' ? '12px' : '16px',
  lineHeight: density === 'compact' ? '16px' : '24px',
  color: tokens[colorScheme].tabInactiveText,
  whiteSpace: 'nowrap',
})

export const paginationPerPageSelectStyles = (
  colorScheme: ColorScheme,
  density: TableDensity = 'default'
): SystemStyleObject => ({
  bg: tokens[colorScheme].inputBg,
  color: tokens[colorScheme].inputText,
  fontSize: density === 'compact' ? '12px' : '14px',
  lineHeight: density === 'compact' ? '16px' : '20px',
  fontWeight: '500',
  fontFamily: "'Soehne', 'Söhne', sans-serif",
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: tokens[colorScheme].perPageSelectBorder,
  borderRadius: '0',
  px: density === 'compact' ? '8px' : '10px',
  py: density === 'compact' ? '2px' : '4px',
  minH: density === 'compact' ? '28px' : '36px',
  w: density === 'compact' ? '64px' : '80px',
  cursor: 'pointer',
  outline: 'none',
})

// ─── Checkbox ─────────────────────────────────────────────────────────────────

const checkmarkSvg = (iconColor: string) =>
  `url("data:image/svg+xml,%3Csvg viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M3 8.5L6.5 12L13 4' stroke='${encodeURIComponent(
    iconColor
  )}' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`

const dashSvg = (iconColor: string) =>
  `url("data:image/svg+xml,%3Csvg viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M4 8H12' stroke='${encodeURIComponent(
    iconColor
  )}' stroke-width='2' stroke-linecap='round'/%3E%3C/svg%3E")`

export const tableCheckboxStyles = (
  colorScheme: ColorScheme
): SystemStyleObject => ({
  appearance: 'none',
  width: '16px',
  height: '16px',
  borderWidth: '1.5px',
  borderStyle: 'solid',
  borderColor: tokens[colorScheme].checkboxBorder,
  // Sharp corners — no exceptions (Checkbox spec).
  borderRadius: '0',
  cursor: 'pointer',
  flexShrink: 0,
  _checked: {
    bg: tokens[colorScheme].checkboxBg,
    borderColor: tokens[colorScheme].checkboxBg,
    backgroundImage: checkmarkSvg(tokens[colorScheme].checkboxIcon),
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    backgroundSize: '12px 12px',
  },
  '&:indeterminate, &[data-indeterminate]': {
    bg: tokens[colorScheme].checkboxBg,
    borderColor: tokens[colorScheme].checkboxBg,
    backgroundImage: dashSvg(tokens[colorScheme].checkboxIcon),
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    backgroundSize: '12px 12px',
  },
  _focusVisible: {
    outline: '2px solid',
    outlineColor: tokens[colorScheme].focusRing,
    outlineOffset: '2px',
  },
})
